import type {
  ChartConfig,
  ConfigDrivenReportConfig,
  StatCardConfig,
} from "@hooks/use-reports/types";
import type { UserReportConfig } from "@hooks/use-get/report-user-config";

/**
 * Applies user configuration overrides to a report config
 * Filters hidden items and reorders based on user preferences
 */
export const applyUserConfigToReport = (
  reportConfig: ConfigDrivenReportConfig,
  userConfig: UserReportConfig | null
): ConfigDrivenReportConfig => {
  // If no user config, return report as-is
  if (!userConfig?.override_config) {
    return reportConfig;
  }

  const {
    charts: chartsOverride = {},
    statCards: cardsOverride = {},
    addedCharts = {},
    addedStatCards = {},
  } = userConfig.override_config;

  const mergedCharts: ChartConfig[] = [
    ...(reportConfig.charts || []),
    ...Object.entries(addedCharts).map(([id, config]) => ({
      ...config,
      id,
    })),
  ];

  const filteredCharts = mergedCharts
    .filter((chart) => !chartsOverride[chart.id]?.hidden)
    .sort((a, b) => {
      const orderA = chartsOverride[a.id]?.order ?? mergedCharts.indexOf(a);
      const orderB = chartsOverride[b.id]?.order ?? mergedCharts.indexOf(b);
      return orderA - orderB;
    });

  const mergedStatCards: StatCardConfig[] = [
    ...(reportConfig.statCards || []),
    ...Object.entries(addedStatCards).map(([id, config]) => ({
      ...config,
      id,
    })),
  ];

  const filteredStatCards = mergedStatCards
    .filter((card) => !cardsOverride[card.id]?.hidden)
    .sort((a, b) => {
      const orderA = cardsOverride[a.id]?.order ?? mergedStatCards.indexOf(a);
      const orderB = cardsOverride[b.id]?.order ?? mergedStatCards.indexOf(b);
      return orderA - orderB;
    });

  return {
    ...reportConfig,
    charts: filteredCharts,
    statCards: filteredStatCards,
  };
};
